import React, { useState, useEffect } from 'react';
import './BirthdayCelebration.css';

const BirthdayCelebration = ({ userName, messages, onComplete }) => {
  const [showCelebration, setShowCelebration] = useState(true);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [audioContext, setAudioContext] = useState(null);

  const celebrationMessages = messages || [
    `🎉 Happy Birthday ${userName}! 🎉`,
    `🎂 Shahji Computers की तरफ से 🎂`,
    `🎈 जन्मदिन की हार्दिक शुभकामनाएं! 🎈`,
    `🌟 May all your dreams come true! 🌟`,
    `🎊 Have a wonderful year ahead! 🎊`
  ];

  const birthdayEmojis = ['🎉', '🎂', '🎈', '🎊', '🌟', '🎁', '🍰', '🎵', '💖', '✨'];

  // Play birthday music from audio file - Auto play without controls
  const playBirthdayMusic = () => {
    try {
      const audio = new Audio('/happy-birthday-song.mp3');
      audio.volume = 0.8;
      audio.preload = 'auto';
      setAudioContext(audio);
      
      // Event listener for when audio ends - Complete celebration
      audio.addEventListener('ended', () => {
        console.log('🎵 Birthday music ended (29s), completing celebration');
        setShowCelebration(false);
        onComplete();
      });
      
      // Event listener for loading errors
      audio.addEventListener('error', (e) => {
        console.error('❌ Audio loading error:', e);
        console.error('Error details:', {
          code: audio.error?.code,
          message: audio.error?.message,
          src: audio.src
        });
        // Even if audio fails, complete celebration after 29 seconds
        setTimeout(() => {
          console.log('⏰ Audio failed, completing celebration after 29s fallback');
          setShowCelebration(false);
          onComplete();
        }, 29000);
      });
      
      // Auto-play music immediately
      const playMusic = async () => {
        try {
          console.log('🎵 Auto-playing birthday music...');
          await audio.play();
          console.log('✅ Birthday music playing successfully (29 seconds)');
        } catch (error) {
          console.log('⚠️ Audio autoplay prevented:', error.message);
          // If autoplay fails, try to play with user interaction
          // Create a click listener on the document to start audio
          const startAudio = () => {
            audio.play().then(() => {
              console.log('✅ Audio started after user interaction');
              document.removeEventListener('click', startAudio);
            }).catch(err => {
              console.error('❌ Failed to start audio even with user interaction:', err);
            });
          };
          document.addEventListener('click', startAudio, { once: true });
          console.log('💡 Audio will start on first user interaction');
        }
      };
      
      // Play immediately
      playMusic();
      
      return audio;
    } catch (error) {
      console.error('❌ Audio creation failed:', error);
      // Fallback timer if audio completely fails
      setTimeout(() => {
        console.log('⏰ Audio creation failed, completing celebration after 29s fallback');
        setShowCelebration(false);
        onComplete();
      }, 29000);
      return null;
    }
  };
  


  useEffect(() => {
    // Auto-start music immediately
    playBirthdayMusic();

    // Message rotation
    const messageInterval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % celebrationMessages.length);
    }, 2000);

    return () => {
      clearInterval(messageInterval);
      if (audioContext) {
        audioContext.pause();
        audioContext.currentTime = 0;
      }
    };
  }, [userName, onComplete, celebrationMessages.length]);

  if (!showCelebration) return null;

  return (
    <div className="birthday-celebration-container">
      {/* Background Animation */}
      <div className="birthday-background">
        <div className="confetti-container">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: `hsl(${Math.random() * 360}, 70%, 60%)`
              }}
            />
          ))}
        </div>

        {/* Floating Emojis */}
        <div className="emoji-container">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="floating-emoji"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                fontSize: `${Math.random() * 20 + 30}px`
              }}
            >
              {birthdayEmojis[Math.floor(Math.random() * birthdayEmojis.length)]}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="birthday-content">
        {/* Company Logo/Name */}
        <div className="company-header">
          <div className="logo-container">
            <img 
              src="/shahji-logo.png" 
              alt="Shahji Computers" 
              className="company-logo"
            />
            <h1 className="company-name">
              <span className="gradient-text">Shahji Computers</span>
            </h1>
          </div>
          <p className="company-tagline">
            <span className="tagline-icon">🎆</span>
            Your Success, Our Pride
            <span className="tagline-icon">🎆</span>
          </p>
          <div className="celebration-year">🎊 {new Date().getFullYear()} 🎊</div>
        </div>

        {/* Birthday Message */}
        <div className="birthday-message-container">
          <div className="message-glow">
            <div className="birthday-message">
              <h2 className="main-message">{celebrationMessages[currentMessage]}</h2>
            </div>
          </div>
          <div className="sparkle-effects">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="sparkle"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  fontSize: `${Math.random() * 10 + 15}px`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        </div>

        {/* Dancing Characters */}
        <div className="dancing-characters">
          <div className="dancer dancer-1">💃</div>
          <div className="dancer dancer-2">🕺</div>
          <div className="dancer dancer-3">💃</div>
          <div className="dancer dancer-4">🕺</div>
          <div className="dancer dancer-5">🎭</div>
          <div className="dancer dancer-6">🎪</div>
        </div>

        {/* Birthday Cake Animation */}
        <div className="cake-container">
          <div className="cake-glow">
            <div className="birthday-cake">
              🎂
              <div className="candles">
                <span className="candle candle-1">🕯️</span>
                <span className="candle candle-2">🕯️</span>
                <span className="candle candle-3">🕯️</span>
                <span className="candle candle-4">🕯️</span>
                <span className="candle candle-5">🕯️</span>
              </div>
              <div className="cake-sparkles">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className={`cake-sparkle sparkle-${i + 1}`}>✨</div>
                ))}
              </div>
            </div>
          </div>
          <div className="cake-shadow"></div>
        </div>

        {/* Progress Bar */}
        <div className="celebration-progress">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          <p className="progress-text">Celebrating... 🎵 Music Playing ✨</p>
        </div>
      </div>

      {/* Balloons */}
      <div className="balloons-container">
        <div className="balloon balloon-1">🎈</div>
        <div className="balloon balloon-2">🎈</div>
        <div className="balloon balloon-3">🎈</div>
        <div className="balloon balloon-4">🎈</div>
      </div>
    </div>
  );
};

export default BirthdayCelebration;
